import {
  ComponentMultiStyleConfig,
  FormControl,
  FormErrorMessage,
  useMultiStyleConfig,
} from "@chakra-ui/react";
import React, { useMemo } from "react";
import { ComponentStyleProps } from "../../../../lib/styles/props/component-style-props";
import { hasValue } from "../../../../lib/util/common-util";
import { CMFormInputBaseText } from "../../../control";
import { CMButtonFormCommentProps } from "../../comment";
import { CMFormControlHeader } from "../CMFormControlHeader/CMFormControlHeader";
import {
  ChangeBadgeProperties,
  ValidationState,
  ValueObjectMetaString,
} from "../../../../lib";
import { useDomainObjectValidation } from "../../../../hooks/use-domain-object-validation";
import { useMessageDictionary } from "../../../../hooks/use-message-dictionary";
import { FormRequiredLabelProps } from "../../label";

export type CMFormInputTextProps = {
  editMode?: "editable" | "disabled" | "readOnly";
  size?: "sm" | "md" | "lg";

  /**
   * trueなら、ヘッダなしとなる。
   * この場合、changeBadgePropertiesやcommentButtonPropsが存在しても、変更やコメントを扱わない。
   */
  noHeader?: boolean;

  /**
   * 値オブジェクトメタデータ
   */
  valueObjectMeta: ValueObjectMetaString;

  label?: string;
  placeholder?: string;

  /**
   * 値。
   */
  value?: string | null;

  /**
   * 必須マークの設定。
   * この項目が設定されている場合は必須ラベルを表示する。
   */
  requiredLabelProps?: FormRequiredLabelProps;

  /**
   * 変更バッジのprops。
   * 値があれば、このコントロールは差分モード(差分があれば変更バッジとオレンジ枠を表示するモード)となる。
   */
  changeBadgeProperties?: ChangeBadgeProperties<string>;

  /**
   * コメントボタンのprops。
   * これが存在すれば、コメントボタンを表示する。
   */
  commentButtonProps?: CMButtonFormCommentProps;

  /**
   * 値の変更を通知する。
   */
  onChange?: (value: string) => void;

  /**
   * バリデーションを行ったタイミングで、その結果を通知する。
   */
  onValidate?: (validationState: ValidationState<string>) => void;
} & ComponentStyleProps;

/**
 * フォームテキストインプットを表示するコンポーネント
 */
export const CMFormInputText = ({
  editMode = "editable",
  size = "md",

  noHeader = false,

  valueObjectMeta,

  label,
  placeholder,
  value = null,

  requiredLabelProps,

  changeBadgeProperties,

  commentButtonProps,

  onChange,
  onValidate,

  sx,
  ...rest
}: CMFormInputTextProps) => {
  const styles = useMultiStyleConfig("CMFormInputTextStyles");
  const dict = useMessageDictionary();

  const { validate, validationState } = useDomainObjectValidation(
    value,
    valueObjectMeta,
    onValidate,
  );
  const isDiffMode = useMemo(
    () => hasValue(changeBadgeProperties),
    [changeBadgeProperties],
  );

  const containerStyles = useMemo(
    () => ({
      ...styles.containerBase,
      ...(editMode === "readOnly" ? styles.containerReadOnly : undefined),
      ...sx,
    }),
    [styles.containerBase, styles.containerReadOnly, editMode, sx],
  );

  return (
    <FormControl
      isInvalid={validationState.state === "error"}
      sx={containerStyles}
      {...rest}
    >
      {!noHeader && (
        <CMFormControlHeader<string>
          label={label}
          value={value}
          requiredLabelProps={requiredLabelProps}
          changeBadgeProperties={changeBadgeProperties}
          commentButtonProps={commentButtonProps}
          sx={styles.header}
        />
      )}
      <CMFormInputBaseText
        editMode={editMode}
        variant={
          validationState.state === "error"
            ? "error"
            : isDiffMode
            ? "diff"
            : "default"
        }
        size={size}
        placeholder={placeholder ?? dict(valueObjectMeta.placeholder)}
        value={value}
        changedFrom={changeBadgeProperties?.changedFrom}
        onChange={onChange}
        onBlur={validate}
        sx={styles.input}
      />
      {validationState.state === "error" &&
        editMode !== "readOnly" &&
        validationState.errors.length > 0 && (
          <FormErrorMessage sx={styles.error}>
            {validationState.errors[0].errorMessage}
          </FormErrorMessage>
        )}
    </FormControl>
  );
};

export const CMFormInputTextStyles: ComponentMultiStyleConfig = {
  parts: ["containerBase", "containerReadOnly", "header", "input", "error"],
  baseStyle: {
    containerBase: {},
    containerReadOnly: {},
    header: {
      mb: "2px",
    },
    input: {},
    error: {
      mt: "4px",
    },
  },
};
